import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Select, Space, Statistic, Table, Tag, message } from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { getErrorText } from "../../utils/errorText";
import { formatDateTime } from "../../utils/adminFormat";

interface CallLogItem {
  id: number;
  userId: number;
  projectId: number;
  agentId: number;
  action: string;
  modelName: string;
  channelId: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cost: number;
  createdAt: string;
}

interface Aggregate {
  totalTokens: number;
  totalCost: number;
  callCount: number;
}

interface ChannelOption {
  id: number;
  name: string;
}

/**
 * ModelCallLogsTab 模型网关调用日志(PRD v4 01 章 R2-B):
 * 读 /api/v3/admin/model-call-logs,支持渠道/模型/动作过滤,顶部展示聚合。
 */
const ModelCallLogsTab: React.FC<{ channels: ChannelOption[] }> = ({ channels }) => {
  const { API_BASE_URL } = useGlobal();
  const [items, setItems] = useState<CallLogItem[]>([]);
  const [aggregate, setAggregate] = useState<Aggregate | null>(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [channelId, setChannelId] = useState<number>();
  const [modelName, setModelName] = useState("");
  const [action, setAction] = useState("");

  const fetchLogs = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true);
      try {
        const res = await request.get(`${API_BASE_URL}/api/v3/admin/model-call-logs`, {
          params: {
            page,
            page_size: pageSize,
            channel_id: channelId || undefined,
            model_name: modelName || undefined,
            action: action || undefined,
          },
        });
        const data = res.data?.data;
        setItems(data?.list || []);
        setAggregate(data?.aggregate || null);
        setPagination({ current: page, pageSize, total: data?.total || 0 });
      } catch (e) {
        message.error(getErrorText(e, "加载调用日志失败"));
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL, channelId, modelName, action]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const channelName = (id: number) => channels.find((c) => c.id === id)?.name || (id ? `渠道 ${id}` : "-");

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          style={{ minWidth: 180 }}
          allowClear
          placeholder="按渠道过滤"
          value={channelId}
          options={channels.map((c) => ({ label: c.name, value: c.id }))}
          onChange={setChannelId}
        />
        <Input
          style={{ width: 180 }}
          placeholder="模型名"
          allowClear
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
        />
        <Input
          style={{ width: 160 }}
          placeholder="动作(如 agent_chat)"
          allowClear
          value={action}
          onChange={(e) => setAction(e.target.value)}
        />
        <Button onClick={() => fetchLogs(1, pagination.pageSize)} loading={loading}>
          查询
        </Button>
        {aggregate && (
          <Space size="large" style={{ marginLeft: 16 }}>
            <Statistic title="调用次数" value={aggregate.callCount} valueStyle={{ fontSize: 16 }} />
            <Statistic title="总 Tokens" value={aggregate.totalTokens} valueStyle={{ fontSize: 16 }} />
            <Statistic title="总成本" value={aggregate.totalCost.toFixed(4)} valueStyle={{ fontSize: 16 }} />
          </Space>
        )}
      </Space>
      <Table<CallLogItem>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => fetchLogs(page, pageSize),
        }}
        columns={[
          { title: "时间", dataIndex: "createdAt", width: 160, render: (v) => formatDateTime(v) },
          { title: "渠道", dataIndex: "channelId", width: 140, render: (v) => <Tag>{channelName(v)}</Tag> },
          { title: "模型", dataIndex: "modelName", ellipsis: true },
          { title: "动作", dataIndex: "action", width: 150 },
          { title: "用户", dataIndex: "userId", width: 80 },
          { title: "Prompt", dataIndex: "promptTokens", width: 90 },
          { title: "Completion", dataIndex: "completionTokens", width: 100 },
          { title: "成本", dataIndex: "cost", width: 90, render: (v) => (v ? v.toFixed(4) : "-") },
        ]}
      />
    </div>
  );
};

export default ModelCallLogsTab;
